import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ButtonGroup,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import { saveShortcutLabel } from '@/hooks/useSaveShortcut';
import { usePermissions } from '@/components/PermissionGate';
import { getSaveMenuExtras } from '@/config/saveMenuExtras';

interface SaveMenuButtonProps {
  /** Aggregate-root entity name, e.g. "PlanScenario" — keys the saveMenuExtras registry. */
  entity: string;
  /** Row id; undefined on the create view — dropdown items need a saved record and are hidden. */
  entityId?: string;
  /** The form's primary save handler (the main button action). */
  onSave: () => void;
  isSaving?: boolean;
  saveDisabled?: boolean;
}

/**
 * The generated DetailViews' Save button. With no registered saveMenuExtras (or none the user may
 * see, or no saved record yet) it renders the plain Save button unchanged; otherwise it becomes a
 * split button whose ▾ dropdown carries the registered "capture this page" actions — the same
 * shape as the template split button on templatable entities.
 */
export function SaveMenuButton({
  entity,
  entityId,
  onSave,
  isSaving = false,
  saveDisabled = false,
}: SaveMenuButtonProps) {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDialogId, setActiveDialogId] = useState<string | null>(null);

  const extras = entityId
    ? getSaveMenuExtras(entity).filter(
        (extra) => !extra.permission || permissions.includes(extra.permission),
      )
    : [];

  const saveLabel = isSaving ? t('common.saving') : t('common.save');
  const inactiveSx = !saveDisabled ? {} : { borderColor: 'grey.300', color: 'grey.400' };

  if (extras.length === 0) {
    return (
      <Tooltip title={saveShortcutLabel()}>
        <span>
          <Button
            variant="outlined"
            onClick={onSave}
            disabled={saveDisabled}
            sx={{ textTransform: 'none', fontWeight: 500, px: 2, ...inactiveSx }}
          >
            {saveLabel}
          </Button>
        </span>
      </Tooltip>
    );
  }

  return (
    <>
      {/* Tooltip wraps the whole group: ButtonGroup styles its direct children, so a wrapper
          inside the group would break the joined look (same trade-off as the template button). */}
      <Tooltip title={saveShortcutLabel()}>
        <ButtonGroup
          ref={anchorRef}
          variant="outlined"
          sx={{ '& .MuiButtonGroup-grouped': { textTransform: 'none', fontWeight: 500 } }}
        >
          <Button onClick={onSave} disabled={saveDisabled} sx={{ px: 2, ...inactiveSx }}>
            {saveLabel}
          </Button>
          <Button size="small" onClick={() => setMenuOpen(true)} aria-label="save options">
            <ArrowDropDown />
          </Button>
        </ButtonGroup>
      </Tooltip>
      <Menu anchorEl={anchorRef.current} open={menuOpen} onClose={() => setMenuOpen(false)}>
        {extras.map((extra) => (
          <MenuItem
            key={extra.id}
            onClick={() => {
              setMenuOpen(false);
              setActiveDialogId(extra.id);
            }}
          >
            <ListItemText>{extra.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
      {/* Dialogs hoisted above the menu so they survive it closing. */}
      {extras.map((extra) => (
        <extra.Dialog
          key={extra.id}
          entityId={entityId!}
          open={activeDialogId === extra.id}
          onClose={() => setActiveDialogId(null)}
        />
      ))}
    </>
  );
}
